import { useState, useEffect, useRef, useCallback } from 'react';

import {
  TAB_WIDTH,
  TAB_SPACING,
  DROPDOWN_BUTTON_WIDTH,
  RESIZE_DEBOUNCE_MS,
} from '../constants/chatTabConstants';
import { Chat } from '../types';

/**
 * Calculates which tabs are visible vs hidden based on available container width.
 * Prioritizes showing the active tab and uses a ResizeObserver for responsive layout.
 */
export const useTabVisibility = (chats: Chat[], activeChatId: string) => {
  const [visibleTabs, setVisibleTabs] = useState<Chat[]>([]);
  const [hiddenTabs, setHiddenTabs] = useState<Chat[]>([]);
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const calculateVisibleTabs = useCallback(() => {
    if (chats.length === 0) {
      setVisibleTabs([]);
      setHiddenTabs([]);
      return;
    }

    if (!tabContainerRef.current) {
      setVisibleTabs(chats);
      setHiddenTabs([]);
      return;
    }

    setTimeout(() => {
      if (!tabContainerRef.current || chats.length === 0) return;

      const containerWidth = tabContainerRef.current.offsetWidth;
      if (containerWidth === 0) {
        setVisibleTabs(chats);
        setHiddenTabs([]);
        return;
      }

      // Always prioritize showing the active tab first
      const sortedChats = [...chats];
      const activeTabIndex = sortedChats.findIndex(
        chat => chat.id === activeChatId
      );
      if (activeTabIndex > 0) {
        const activeTab = sortedChats.splice(activeTabIndex, 1)[0];
        sortedChats.unshift(activeTab);
      }

      let visibleCount = 0;
      let remainingWidth = containerWidth;

      for (let i = 0; i < sortedChats.length; i++) {
        const spacing = i > 0 ? TAB_SPACING : 0;
        const isLastPossibleTab = i === sortedChats.length - 1;
        const dropdownSpace = !isLastPossibleTab
          ? DROPDOWN_BUTTON_WIDTH + TAB_SPACING
          : 0;
        const requiredWidth = TAB_WIDTH + spacing + dropdownSpace;

        if (requiredWidth <= remainingWidth) {
          visibleCount = i + 1;
          remainingWidth -= TAB_WIDTH + spacing;
          if (isLastPossibleTab) break;
        } else {
          break;
        }
      }

      if (visibleCount === 0 && sortedChats.length > 0) {
        visibleCount = 1;
      }

      setVisibleTabs(sortedChats.slice(0, visibleCount));
      setHiddenTabs(sortedChats.slice(visibleCount));
    }, 0);
  }, [chats, activeChatId]);

  // Recalculate on chats change
  useEffect(() => {
    if (chats.length === 0) {
      setVisibleTabs([]);
      setHiddenTabs([]);
    } else {
      calculateVisibleTabs();
    }
  }, [chats, activeChatId, calculateVisibleTabs]);

  // ResizeObserver + window resize
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => calculateVisibleTabs(), RESIZE_DEBOUNCE_MS);
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (tabContainerRef.current) {
      resizeObserver.observe(tabContainerRef.current);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateVisibleTabs]);

  return { visibleTabs, hiddenTabs, tabContainerRef };
};
